import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { formatDuration } from '../playlists/Playlists.types';
import { onDemandApi } from './OnDemand.api';
import { ON_DEMAND_KIND_LABEL, OnDemandPlaylist, createdByLabel } from './OnDemand.types';

const REFRESH_MS = 7000;

interface OnDemandQueueProps {
    screenId: string;
    /** Bumped by the wizard after a successful schedule so the queue updates immediately. */
    refreshToken: number;
}

/** Panel 2: playlists waiting on this screen (`status=queued`). */
export const OnDemandQueue: React.FC<OnDemandQueueProps> = ({ screenId, refreshToken }) => {
    const [playlists, setPlaylists] = useState<OnDemandPlaylist[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        try {
            const rows = await onDemandApi.list(screenId, 'queued');
            setPlaylists(rows);
            setError(null);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not load the queue');
        } finally {
            setLoading(false);
        }
    }, [screenId]);

    useEffect(() => {
        void load();
        const timer = window.setInterval(() => void load(), REFRESH_MS);
        return () => window.clearInterval(timer);
    }, [load, refreshToken]);

    return (
        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2, height: '100%' }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6">Queued on this screen</Typography>
                <Tooltip title="Refresh">
                    <Button variant="contained" color="secondary" size="small" onClick={() => void load()} aria-label="Refresh queue" sx={{ minWidth: 36, px: 1 }}>
                        <Refresh fontSize="small" />
                    </Button>
                </Tooltip>
            </Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading && playlists.length === 0 ? (
                <Box display="flex" justifyContent="center" py={4}><CircularProgress color="secondary" /></Box>
            ) : playlists.length === 0 ? (
                <Alert severity="info">Nothing is queued for this screen.</Alert>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Kind</TableCell>
                                <TableCell align="right">Items</TableCell>
                                <TableCell>Total time</TableCell>
                                <TableCell>Plays</TableCell>
                                <TableCell>Created by</TableCell>
                                <TableCell>Created</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {playlists.map((playlist) => (
                                <TableRow key={playlist._id}>
                                    <TableCell>{playlist.name}</TableCell>
                                    <TableCell><Chip size="small" label={ON_DEMAND_KIND_LABEL[playlist.kind] || playlist.kind} /></TableCell>
                                    <TableCell align="right">{playlist.items.length}</TableCell>
                                    <TableCell>{formatDuration(playlist.totalDurationMs)}</TableCell>
                                    <TableCell>{playlist.playCount} of {playlist.maxPlays}</TableCell>
                                    <TableCell>{createdByLabel(playlist)}</TableCell>
                                    <TableCell>{playlist.createdAt ? new Date(playlist.createdAt).toLocaleString() : '—'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Paper>
    );
};
